import type { QueryClient } from "@tanstack/react-query";

import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { aiAgentQueryKeys } from "../../../queries/queryKeyFactory";
import type { ThreadOptions } from "../services/thread/types";

const getValidThreadId = (queryClient: QueryClient, options: ThreadOptions = {}): string | null => {
    const isThreadPending = queryClient.isFetching({
        queryKey: aiAgentQueryKeys.leadGenerator.thread(options.savedSearchId),
    });
    const threadId = queryClient.getQueryData<string>(aiAgentQueryKeys.leadGenerator.thread(options.savedSearchId));

    return !is.undefined(threadId) && !isThreadPending ? threadId : null;
};

type ThreadValidationResult = { isValid: false } | { isValid: true; threadId: string; numberOfFetchingQueries: number };

/**
 * Common thread validation logic for Lead Generator thread
 */
export const validateLeadGeneratorThread = (
    queryClient: QueryClient,
    options?: ThreadOptions,
): ThreadValidationResult => {
    const threadId = getValidThreadId(queryClient, options);

    if (!threadId) {
        return { isValid: false };
    }

    const numberOfFetchingQueries = queryClient.isFetching({
        queryKey: aiAgentQueryKeys.leadGenerator.chat(threadId),
    });

    return { isValid: true, threadId, numberOfFetchingQueries };
};

type CancelQueriesOptions = {
    threadId: string;
};

/**
 * Cancels ongoing queries for a given thread with error handling
 */
const cancelThreadQueries = async (
    queryClient: QueryClient,
    options: CancelQueriesOptions,
): Promise<boolean> => {
    const { threadId } = options;

    try {
        await queryClient.cancelQueries({
            queryKey: aiAgentQueryKeys.leadGenerator.chat(threadId),
        });

        return true;
    } catch (err) {
        sentryErrorTrackerWithScope({
            errorMessage: "Error canceling queries",
            e: err,
            params: {
                extra: {
                    threadId,
                },
            },
        });

        return false;
    }
};

type ThreadValidationAndCancellationResult =
    | {
          success: false;
      }
    | {
          success: true;
          threadId: string;
      };

/**
 * Validates thread and cancels ongoing queries if needed
 */
export const validateAndCancelIfNeeded = async (
    queryClient: QueryClient,
    options?: ThreadOptions,
): Promise<ThreadValidationAndCancellationResult> => {
    const threadValidationResult = validateLeadGeneratorThread(queryClient, options);

    if (!threadValidationResult.isValid) {
        return { success: false };
    }

    const { threadId, numberOfFetchingQueries } = threadValidationResult;

    // Cancel ongoing queries if there are any (prioritize new request)
    if (numberOfFetchingQueries > 0) {
        const isSuccess = await cancelThreadQueries(queryClient, {
            threadId,
        });

        if (!isSuccess) {
            return { success: false };
        }
    }

    return { success: true, threadId };
};
